/**
 * Module dependencies.
 */

import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover';
import { Spinner } from 'app/components/ui/spinner';
import { Transaction } from 'app/types/transaction';
import { Check, Plus, Settings2, Tag } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAttachBadge,
  useDetachBadge,
  useTransactionBadges
} from 'app/hooks/requests/bank-accounts/use-transaction-badges';
import { ManageBadgesDialog } from './manage-badges-dialog';

/**
 * `Props` type.
 */

type Props = {
  item: Transaction;
};

/**
 * Export `BadgeAssignPopover` — the per-row "+" that toggles which badges are on
 * this transaction, and opens the manage dialog.
 */

export function BadgeAssignPopover({ item }: Props): JSX.Element {
  const [translate] = useTranslation();
  const { data: badges, isLoading } = useTransactionBadges();
  const attach = useAttachBadge();
  const detach = useDetachBadge();
  const [open, setOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  const attachedIds = new Set((item.badges ?? []).map(badge => badge.id));

  const toggle = (badgeId: number) => {
    if (attachedIds.has(badgeId)) {
      detach.mutate({ badgeId, transactionId: item.id });
    } else {
      attach.mutate({ badgeId, transactionId: item.id });
    }

    // Close the menu after picking a badge.
    setOpen(false);
  };

  return (
    <>
      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild>
          <button
            aria-label={translate('bankAccounts.movements.badges.add')}
            className={
              'inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:border-foreground hover:text-foreground'
            }
            onClick={event => event.stopPropagation()}
            title={translate('bankAccounts.movements.badges.add')}
            type={'button'}
          >
            <Plus className={'h-3 w-3'} />

            <Tag className={'h-3 w-3'} />
          </button>
        </PopoverTrigger>

        <PopoverContent align={'start'} className={'w-64 p-1'} onClick={event => event.stopPropagation()}>
          <div className={'max-h-64 overflow-y-auto'}>
            {isLoading ? (
              <div className={'grid place-items-center py-4'}>
                <Spinner />
              </div>
            ) : (badges ?? []).length === 0 ? (
              <p className={'px-2 py-3 text-center text-xs text-muted-foreground'}>
                {translate('bankAccounts.movements.badges.empty')}
              </p>
            ) : (
              (badges ?? []).map(badge => {
                const active = attachedIds.has(badge.id);

                return (
                  <button
                    className={
                      'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent'
                    }
                    key={badge.id}
                    onClick={() => toggle(badge.id)}
                    type={'button'}
                  >
                    <span
                      className={'h-3.5 w-3.5 shrink-0 rounded-full border border-black/10'}
                      style={{ backgroundColor: badge.color }}
                    />

                    <span className={'min-w-0 flex-1 truncate'}>{badge.name}</span>

                    {active && <Check className={'h-4 w-4 shrink-0 text-primary'} />}
                  </button>
                );
              })
            )}
          </div>

          <div className={'mt-1 border-t border-border pt-1'}>
            <button
              className={
                'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground hover:bg-accent hover:text-foreground'
              }
              onClick={() => {
                setOpen(false);
                setManageOpen(true);
              }}
              type={'button'}
            >
              <Settings2 className={'h-4 w-4'} />

              {translate('bankAccounts.movements.badges.manage')}
            </button>
          </div>
        </PopoverContent>
      </Popover>

      <ManageBadgesDialog onOpenChange={setManageOpen} open={manageOpen} />
    </>
  );
}
